<template>
    <div :class="[backgroundClass, borderTopClasses, borderBottomClasses, columnWidthClass]">
        <div class="size-full" :class="[borderLeftClasses, borderRightClasses]" />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { ConflictMarker } from '../Timeline.types';
import { columnWidthClass } from '../sharedStyles';

type StaticCellProps = {
    isInHoveredColumn: boolean;
    isInHoveredRow: boolean;
    isConflictHovered: boolean;
    isWorkDay: boolean;
    conflictMarker: ConflictMarker;
};

const props = defineProps<StaticCellProps>();

const hasConflict = computed(() => props.conflictMarker !== 'No-Conflict');

const backgroundClass = computed(() => {
    // This very cell is hovered
    if (props.isInHoveredColumn && props.isInHoveredRow) {
        if (hasConflict.value) {
            return 'bg-red-400';
        }
        return 'bg-blue-400';
    }

    // A cell in either the column or the row is hovered
    if (props.isInHoveredColumn || props.isInHoveredRow) {
        if (props.isConflictHovered) {
            return 'bg-red-100';
        } else {
            return 'bg-blue-100';
        }
    }

    if (!props.isWorkDay) {
        return 'bg-gray-100';
    }

    return 'bg-gray-50';
});

const borderLeftClasses = computed(() => {
    const classes: string[] = [];

    // Conflict border (takes precedence with z-index)
    if (props.conflictMarker === 'Conflict-Start' || props.conflictMarker === 'Conflict-Single') {
        classes.push('cell-border-left cell-border-left-2 cell-border-left-red-700 cell-border-left-z-20');
    }

    // Left border of hovered column
    if (props.isInHoveredColumn) {
        if (props.isConflictHovered) {
            classes.push('cell-border-left cell-border-left-1 cell-border-left-red-200 cell-border-left-z-1');
        } else {
            classes.push('cell-border-left cell-border-left-1 cell-border-left-blue-200 cell-border-left-z-1');
        }
    }

    return classes;
});

const borderRightClasses = computed(() => {
    const classes: string[] = [];

    // Conflict border (takes precedence with z-index)
    if (props.conflictMarker === 'Conflict-End' || props.conflictMarker === 'Conflict-Single') {
        classes.push('cell-border-right cell-border-right-2 cell-border-right-red-700 cell-border-right-z-20');
    }

    // Right border of hovered column + "Middle" (technically: right) borders in hovered row
    if (props.isInHoveredColumn || props.isInHoveredRow) {
        if (props.isConflictHovered) {
            classes.push('cell-border-right cell-border-right-1 cell-border-right-red-200 cell-border-right-z-1');
        } else {
            classes.push('cell-border-right cell-border-right-1 cell-border-right-blue-200 cell-border-right-z-1');
        }
    }

    return classes;
});

const borderTopClasses = computed(() => {
    const classes: string[] = [];

    // Conflict border (takes precedence with z-index)
    if (hasConflict.value) {
        classes.push('cell-border-top cell-border-top-2 cell-border-top-red-700 cell-border-top-z-20');
    }

    // Top border of hovered row
    if (props.isInHoveredRow) {
        if (props.isConflictHovered) {
            classes.push('cell-border-top cell-border-top-1 cell-border-top-red-200');
        } else {
            classes.push('cell-border-top cell-border-top-1 cell-border-top-blue-200');
        }
    }

    return classes;
});

const borderBottomClasses = computed(() => {
    const classes: string[] = [];

    // Conflict border (takes precedence with z-index)
    if (hasConflict.value) {
        classes.push('cell-border-bottom cell-border-bottom-2 cell-border-bottom-red-700 cell-border-bottom-z-20');
    }

    // Bottom border of hovered row + "Middle" (technically: bottom) borders in hovered column
    if (props.isInHoveredRow || props.isInHoveredColumn) {
        if (props.isConflictHovered) {
            classes.push('cell-border-bottom cell-border-bottom-1 cell-border-bottom-red-200');
        } else {
            classes.push('cell-border-bottom cell-border-bottom-1 cell-border-bottom-blue-200');
        }
    }

    return classes;
});
</script>

<style scoped>
/**
 * Using CSS' border property causes the following issues:
 * - The intersection between a 1px and a 2px border creates a tiny triangle which looks ugly
 * - The visual border is created by the background color of the CSS grid "shining through", so the cell border must be placed 1px outside of the actual component border
 *
 * Therefore, the implementation uses 2 nested elements, each having a ::before and ::after pseudo element, so we have in total 4 pseudo elements, one for each edge:
 */

.cell-border-left,
.cell-border-right,
.cell-border-top,
.cell-border-bottom {
    position: relative;
}

.cell-border-left::before {
    content: '';
    position: absolute;
    height: calc(100% + 2px);
    top: -1px;
    left: -1px;
}

.cell-border-right::after {
    content: '';
    position: absolute;
    height: calc(100% + 2px);
    top: -1px;
    right: -1px;
}

.cell-border-top::before {
    content: '';
    position: absolute;
    width: calc(100% + 2px);
    top: -1px;
    left: -1px;
}

.cell-border-bottom::after {
    content: '';
    position: absolute;
    width: calc(100% + 2px);
    bottom: -1px;
    left: -1px;
}

.cell-border-left-blue-200::before {
    background-color: var(--color-blue-200);
}

.cell-border-left-red-200::before {
    background-color: var(--color-red-200);
}

.cell-border-left-red-700::before {
    background-color: var(--color-red-700);
}

.cell-border-left-1::before {
    width: 1px;
}

.cell-border-left-2::before {
    width: 2px;
}

.cell-border-left-z-1::before {
    z-index: 1;
}

.cell-border-left-z-20::before {
    z-index: 20;
}

.cell-border-right-blue-200::after {
    background-color: var(--color-blue-200);
}

.cell-border-right-red-200::after {
    background-color: var(--color-red-200);
}

.cell-border-right-red-700::after {
    background-color: var(--color-red-700);
}

.cell-border-right-1::after {
    width: 1px;
}

.cell-border-right-2::after {
    width: 2px;
}

.cell-border-right-z-1::after {
    z-index: 1;
}

.cell-border-right-z-20::after {
    z-index: 20;
}

.cell-border-top-blue-200::before {
    background-color: var(--color-blue-200);
}

.cell-border-top-red-200::before {
    background-color: var(--color-red-200);
}

.cell-border-top-red-700::before {
    background-color: var(--color-red-700);
}

.cell-border-top-1::before {
    height: 1px;
}

.cell-border-top-2::before {
    height: 2px;
}

.cell-border-top-z-20::before {
    z-index: 20;
}

.cell-border-bottom-blue-200::after {
    background-color: var(--color-blue-200);
}

.cell-border-bottom-red-200::after {
    background-color: var(--color-red-200);
}

.cell-border-bottom-red-700::after {
    background-color: var(--color-red-700);
}

.cell-border-bottom-1::after {
    height: 1px;
}

.cell-border-bottom-2::after {
    height: 2px;
}

.cell-border-bottom-z-20::after {
    z-index: 20;
}
</style>
